import { col, NETWORK_DIRECT, RESERVOIR_DIRECT, type Shape } from './base.ts';

export const JOINT_OUTPUTS_RELATION: Shape = [
  NETWORK_DIRECT,
  col('jointEntity', 'unitless', 'Reference to the junction/manifold entity.'),
];

/**
 * The network-grain relations carried by `SeparatorOutputs_Network_Agent`
 * The parent-`network` direct-relation (the NETWORK grain ref) plus
 * the separator entity ref. Kept verbatim (`prefix:''` → no rename). As with
 * {@link JOINT_OUTPUTS_RELATION}, the source's `entityName` is a `z.json()`
 * blob (→ `unknownCol`) and is excluded.
 */
export const SEPARATOR_OUTPUTS_RELATION: Shape = [
  NETWORK_DIRECT,
  col('separatorEntity', 'unitless', 'Reference to the separator entity.'),
];

/**
 * The reservoir-grain relations carried by `ReservoirOutputs_Agent`:
 * the `reservoir` direct-relation (the RESERVOIR grain ref) plus the parent
 * `field` ref. Both kept verbatim (`prefix:''` → no rename). NOTE the source
 * names the grain ref `reservoir` (NOT `reservoirEntity`), so the daily-rollup
 * {@link RESERVOIR_RELATION} (whose suffix is `reservoirEntity`) does NOT match
 * here.
 */
export const RESERVOIR_OUTPUTS_RELATION: Shape = [
  RESERVOIR_DIRECT,
  col('field', 'unitless', 'Reference to the parent field model entity.'),
];

export const JOINT_OUTPUTS: Shape = [
  col('oilRate', 'liquidRate', 'Oil production rate at the junction.', { suffix: 'nmvJointOilRate' }),
  col('gasRate', 'gasRate', 'Gas production rate at the junction.', { suffix: 'nmvJointGasRate' }),
  col('waterRate', 'liquidRate', 'Water production rate at the junction.', { suffix: 'nmvWaterRate' }),
  col('liquidRate', 'liquidRate', 'Total liquid production rate at the junction.', { suffix: 'nmvJointLiquidRate' }),
  col('pressure', 'pressureAbsolute', 'Operating pressure at the junction.', { suffix: 'nmvJointPressure' }),
  col('temperature', 'temperature', 'Operating temperature at the junction.', { suffix: 'nmvJointTemperature' }),
  col('co2', 'fraction', 'CO2 content at the junction.', { suffix: 'nmvJointCO2' }),
  col('n2', 'fraction', 'N2 content at the junction.', { suffix: 'nmvJointN2' }),
  col('h2s', 'fraction', 'H2S content at the junction.', { suffix: 'nmvJointH2S' }),
  col('violatingConstraintsFlag', 'unitless', 'Junction operating outside its constraints (0/1).', {
    suffix: 'nmvJointViolatingConstraintsFlag',
  }),
  col('dp', 'pressureDelta', 'Differential pressure across the junction.', { suffix: 'nmvDp' }),
  col('gor', 'gasOilRatio', 'Gas-oil ratio at the junction.', { suffix: 'nmvGor' }),
  col('cgr', 'liquidGasRatio', 'Condensate-gas ratio at the junction.', { suffix: 'nmvCgr' }),
  col('waterCut', 'fraction', 'Water cut at the junction.', { suffix: 'nmvWaterCut' }),
  col('wgr', 'liquidGasRatio', 'Water-gas ratio at the junction.', { suffix: 'nmvWgr' }),
  col('glr', 'gasOilRatio', 'Gas-liquid ratio at the junction.', { suffix: 'nmvGlr' }),
  col('pdown', 'pressureAbsolute', 'Downstream pressure.', { suffix: 'nmvPdown' }),
  col('tdown', 'temperature', 'Downstream temperature.', { suffix: 'nmvTdown' }),
  col('injectionWaterRate', 'liquidRate', 'Water injection rate at the junction.', { suffix: 'nmvInjectionWaterRate' }),
  col('injectionGasRate', 'gasRate', 'Gas injection rate at the junction.', { suffix: 'nmvInjectionGasRate' }),
];

export const SEPARATOR_OUTPUTS: Shape = [
  col('oilRate', 'liquidRate', 'Oil production rate at the separator.', { suffix: 'nmvSeparatorOilRate' }),
  col('gasRate', 'gasRate', 'Gas production rate at the separator.', { suffix: 'nmvSeparatorGasRate' }),
  col('waterRate', 'liquidRate', 'Water production rate at the separator.', { suffix: 'nmvSeparatorWaterRate' }),
  col('liquidRate', 'liquidRate', 'Total liquid rate at the separator.', { suffix: 'nmvSeparatorLiquidRate' }),
  col('violatingConstraintsFlag', 'unitless', 'Separator operating outside its constraints (0/1).', {
    suffix: 'nmvSeparatorViolatingConstraintsFlag',
  }),
  col('limitingConstraintsFlag', 'unitless', 'Separator at a limiting constraint (0/1).', {
    suffix: 'nmvSeparatorLimitingConstraintsFlag',
  }),
  col('gor', 'gasOilRatio', 'Gas-oil ratio at the separator.', { suffix: 'nmvGor' }),
  col('waterCut', 'fraction', 'Water cut at the separator.', { suffix: 'nmvWaterCut' }),
  col('cgr', 'liquidGasRatio', 'Condensate-gas ratio at the separator.', { suffix: 'nmvCgr' }),
  col('pressure', 'pressureAbsolute', 'Separator operating pressure.', { suffix: 'nmvPressure' }),
  col('temperature', 'temperature', 'Separator operating temperature.', { suffix: 'nmvTemperature' }),
  col('glr', 'gasOilRatio', 'Gas-liquid ratio at the separator.', { suffix: 'nmvGlr' }),
  col('wgr', 'liquidGasRatio', 'Water-gas ratio at the separator.', { suffix: 'nmvWgr' }),
  col('oilFMIPercent', 'fraction', 'Oil facility-max-intake utilization.', { suffix: 'nmvOilFMIPercent' }),
  col('gasFMIPercent', 'fraction', 'Gas facility-max-intake utilization.', { suffix: 'nmvGasFMIPercent' }),
  col('waterFMIPercent', 'fraction', 'Water facility-max-intake utilization.', { suffix: 'nmvWaterFMIPercent' }),
  col('measuredOilRate', 'liquidRate', 'Measured oil rate at the separator.', {
    suffix: 'nmvSeparatorMeasuredOilRate',
  }),
  col('measuredGasRate', 'gasRateModel', 'Measured gas rate at the separator.', {
    suffix: 'nmvSeparatorMeasuredGasRate',
  }),
  col('measuredWaterRate', 'liquidRate', 'Measured water rate at the separator.', {
    suffix: 'nmvSeparatoMeasuredrWaterRate',
  }),
];

export const WELL_COUNTS_CORE: Shape = [
  col('wellsActiveOpNf', 'unitless', 'Count of active oil producers on natural flow.'),
  col('wellsActiveGpNf', 'unitless', 'Count of active gas producers on natural flow.'),
  col('wellsActiveOpEsp', 'unitless', 'Count of active oil producers on ESP lift.'),
  col('wellsActiveOpGl', 'unitless', 'Count of active oil producers on gas lift.'),
  col('wellsActiveGi', 'unitless', 'Count of active gas injectors.'),
  col('wellsActiveWi', 'unitless', 'Count of active water injectors.'),
  col('wellsInActiveOpNf', 'unitless', 'Count of inactive oil producers on natural flow.'),
  col('wellsInActiveGpNf', 'unitless', 'Count of inactive gas producers on natural flow.'),
  col('wellsInActiveOpEsp', 'unitless', 'Count of inactive oil producers on ESP lift.'),
  col('wellsInActiveOpGl', 'unitless', 'Count of inactive oil producers on gas lift.'),
  col('wellsInActiveGi', 'unitless', 'Count of inactive gas injectors.'),
  col('wellsInActiveWi', 'unitless', 'Count of inactive water injectors.'),
];

export const FIELD_WELL_COUNTS: Shape = [
  ...WELL_COUNTS_CORE,
  col('wellsActiveWAG', 'unitless', 'Count of active WAG (water-alternating-gas) injectors.'),
  col('wellsInactiveWAG', 'unitless', 'Count of inactive WAG (water-alternating-gas) injectors.'),
];

export const RESERVOIR_VRR: Shape = [
  col('vrr', 'unitless', 'Daily voidage replacement ratio.'),
  col('mtdVRR', 'unitless', 'Month-to-date voidage replacement ratio.'),
  col('ytdVRR', 'unitless', 'Year-to-date voidage replacement ratio.'),
  col('vrrCompliance', 'unitless', 'Daily VRR compliance flag.'),
  col('mtdVRRCompliance', 'unitless', 'Month-to-date VRR compliance flag.'),
  col('ytdVRRCompliance', 'unitless', 'Year-to-date VRR compliance flag.'),
  col('pressureCompliance', 'unitless', 'Reservoir pressure compliance flag.'),
  col('regionPressure', 'pressureAbsolute', 'Average reservoir region pressure.'),
  col('giRate', 'gasRate', 'Aggregated gas injection rate for the reservoir.'),
  col('wiRate', 'liquidRate', 'Aggregated water injection rate for the reservoir.'),
  col('giTargetRate', 'gasRate', 'Gas injection target rate from planning/optimization.'),
  col('wiTargetRate', 'liquidRate', 'Water injection target rate from planning/optimization.'),
  col('giTargetRateCompliance', 'unitless', 'Gas injection target-rate compliance flag.'),
  col('wiTargetRateCompliance', 'unitless', 'Water injection target-rate compliance flag.'),
  col('downholeOilProductionVolume', 'liquidVolume', 'Downhole oil production volume at reservoir conditions.'),
  col('downholeGasProductionVolume', 'gasVolume', 'Downhole gas production volume at reservoir conditions.'),
  col('downholeWaterProductionVolume', 'liquidVolume', 'Downhole water production volume at reservoir conditions.'),
  col('downholeWaterInjectionVolume', 'liquidVolume', 'Downhole water injection volume at reservoir conditions.'),
  col('downholeGasInjectionVolume', 'gasVolume', 'Downhole gas injection volume at reservoir conditions.'),
];

/*
 * ---------------------------------------------------------------------------
 * B1 — agent-facing tail: ten per-string `*_Agent`
 * sources, each fronted by ONE de-prefixed enriched view (`extraRates.ts`).
 * All ten are per-string grain (one `wellString` direct relation) — enriched
 * with well/field/reservoir StringContext. Every measure column is a
 * TimeSeries at the source. The shapes below are the SSOT for their
 * de-prefix/rename/docs. Source typos and irregular casing are preserved
 * VERBATIM in each `suffix` (a no-unknownCol guard test asserts every suffix
 * resolves to a real column).
 * ---------------------------------------------------------------------------
 */

/**
 * Scenario metadata on `OptimizedRates_SRM_String_Agent`: the scenario
 * label/timestamp and the official-approval flag. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
